import { mergeDeep } from '@appbolaget/helpers';

export enum NavigationPlacementEnum {
    Left = 'left',
    Right = 'right',
    Top = 'top',
    Bottom = 'bottom',
    Unplaced = 'unplaced',
}

export const Navigation: { [key: string]: NavigationConfig } = {
    base: {
        navigationType: 'navigation',
        useAttributesInsteadOfMeta: false,
        roleConfig: {
            enabled: false,
        },
        label: {
            singular: 'PAGE_NAVIGATIONS.NAVIGATION',
            plural: 'PAGE_NAVIGATIONS.NAVIGATIONS',
            createNew: 'PAGE_NAVIGATIONS.NEW_NAVIGATION',
        },
        placements: [
            { label: 'PAGE_NAVIGATIONS.PLACEMENTS.LEFT', value: NavigationPlacementEnum.Left },
            { label: 'PAGE_NAVIGATIONS.PLACEMENTS.RIGHT', value: NavigationPlacementEnum.Right },
            { label: 'PAGE_NAVIGATIONS.PLACEMENTS.TOP', value: NavigationPlacementEnum.Top },
            { label: 'PAGE_NAVIGATIONS.PLACEMENTS.BOTTOM', value: NavigationPlacementEnum.Bottom },
            { label: 'PAGE_NAVIGATIONS.PLACEMENTS.UNPLACED', value: NavigationPlacementEnum.Unplaced },
        ],
        connectionTypes: [
            {
                label: 'PAGE_NAVIGATIONS.TYPES.CATEGORY',
                value: 'category',
                model: 'category',
                service: 'categoryService',
                placeholder: 'PAGE_NAVIGATIONS.TYPES.SEARCH_CATEGORY',
                route: 'categories/search',
                parameters: {
                    type: 'category',
                },
            },
            {
                label: 'PAGE_NAVIGATIONS.TYPES.PAGE',
                value: 'page',
                service: 'postService',
                placeholder: 'PAGE_NAVIGATIONS.TYPES.SEARCH_PAGE',
                route: 'posts/search',
                parameters: {
                    type: 'page',
                },
            },
            {
                label: 'PAGE_NAVIGATIONS.TYPES.NEWS',
                value: 'news',
                service: 'postService',
                placeholder: 'PAGE_NAVIGATIONS.TYPES.SEARCH_NEWS',
                route: 'posts/search',
                parameters: {
                    type: 'news',
                },
            },
            {
                label: 'PAGE_NAVIGATIONS.TYPES.EVENT',
                value: 'event',
                service: 'postService',
                placeholder: 'PAGE_NAVIGATIONS.TYPES.SEARCH_EVENT',
                route: 'posts/search',
                parameters: {
                    type: 'occasion',
                },
            },
            {
                label: 'PAGE_NAVIGATIONS.TYPES.APP_LINK',
                value: 'approute',
                service: 'postService',
                placeholder: 'PAGE_NAVIGATIONS.TYPES.SEARCH_APP_LINK',
                route: 'posts/search',
                parameters: {
                    type: 'approute',
                },
            },
        ],

        item: {
            navigationType: 'navigation-item',
            roleConfig: {
                enabled: false,
            },
            colorPicker: {
                enabled: false,
            },
            contentEditor: {
                enabled: false,
            },
            content: {
                enabled: true,
            },
            excerpt: {
                enabled: true,
            },
            expireAt: {
                enabled: true,
            },
            icon: {
                enabled: true,
            },
            media: {
                enabled: true,
            },
            types: [
                {
                    value: 'connection',
                    label: 'PAGE_NAVIGATIONS.TYPES.CONNECTION',
                },
                {
                    value: 'url',
                    label: 'PAGE_NAVIGATIONS.TYPES.URL',
                },
                {
                    value: 'navigation',
                    label: 'PAGE_NAVIGATIONS.TYPES.SUBMENU',
                },
            ],
        },
    },
};

Navigation.faq = mergeDeep({}, Navigation.base, {
    navigationType: 'navigation',
    useAttributesInsteadOfMeta: false,
    label: {
        singular: 'FAQ',
        createNew: 'Ny FAQ'
    },
    item: {
        navigationType: 'navigation-item',
        colorPicker: {
            enabled: false
        },
        contentEditor: {
            enabled: false
        },
        content: {
            enabled: true
        },
        excerpt: {
            enabled: false
        },
        icon: {
            enabled: false
        },
        media: {
            enabled: false
        },
    }
} as NavigationConfig);

Navigation.contentMap = mergeDeep({}, Navigation.base, {
    navigationType: 'content-map',
    useAttributesInsteadOfMeta: true,
    label: {
        singular: 'Utbudskarta',
        createNew: 'Ny utbudskarta',
    },
    item: {
        navigationType: 'content-map-item',
        colorPicker: {
            enabled: true,
        },
        content: {
            enabled: false,
        },
        contentEditor: {
            enabled: true,
        },
    },
} as NavigationConfig);

export interface NavigationConfig {
    /**
     * The category type of the navigation
     */
    navigationType: string;

    connectionTypes: NavigationConnectionType[];

    /**
     * If the role config should be enabled
     */
    roleConfig?: NavigationRoleConfig;

    /**
     * If the item should use attributes instead of meta
     * for icon, url, etc.
     */
    useAttributesInsteadOfMeta: boolean;

    label: {
        singular: string;
        plural: string;
        createNew: string;
    };
    placements: NavigationPlacement[];
    item: NavigationItemConfig;
}

export interface NavigationItemConfig {
    /**
     * The category type of the item
     */
    navigationType: string;

    /**
     * If the role config should be enabled
     */
    roleConfig?: NavigationRoleConfig;

    colorPicker: {
        enabled: boolean;
        requiredPermissions?: string | string[];
    };
    contentEditor: {
        enabled: boolean;
        requiredPermissions?: string | string[];
    };
    content: {
        enabled: boolean;
        requiredPermissions?: string | string[];
    };
    excerpt: {
        enabled: boolean;
        requiredPermissions?: string | string[];
    };
    expireAt: {
        enabled: boolean;
        requiredPermissions?: string | string[];
    };
    icon: {
        enabled: boolean;
        requiredPermissions?: string | string[];
    };
    media: {
        enabled: boolean;
        requiredPermissions?: string | string[];
    };
    types: {
        value: string;
        label: string;
    }[];
}

export interface NavigationConnectionType {
    label: string;
    value: string;
    model?: string;
    service?: string;
    placeholder?: string;
    route?: string;
    parameters?: any;
}

export interface NavigationPlacement {
    label: string;
    value: NavigationPlacementEnum;
}

export interface NavigationRoleConfig {
    enabled: boolean;
    fullCrud?: boolean;
}
